<template>
  <div>
    <div v-if="devices.length" class="mt-11 full-details-wrapper">
      <div class="findings-title px-4 pt-4 pb-4">
        <div class="subtitle1" data-testid="fim-findings-title">
          {{ $t("ticketDetails.findings") }}
        </div>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <div v-for="device in devices" :key="getDeviceKey(device)" class="finding-panel">
        <div
          class="finding-panel-header px-4"
          :data-testid="`fim-finding-header-${getDeviceKey(device)}`"
          @click="toggleDevice(device)"
        >
          <v-icon
            icon="$chevronDown"
            :class="{ rotated: isExpanded(device) }"
            :data-testid="`fim-finding-chevron-${getDeviceKey(device)}`"
          />
          <span class="subtitle1 ml-2" data-testid="fim-finding-hostname">
            {{ device.hostname }}
          </span>
        </div>

        <div
          v-if="isExpanded(device)"
          class="px-4 py-2"
          :data-testid="`fim-finding-content-${getDeviceKey(device)}`"
        >
          <div v-if="device.fileBaselineHash" class="finding-row ml-8 mb-4">
            <span class="subtitle2 finding-label">
              {{ $t("ticketDetails.fullDetails.fileIntegrityViolation.baselineHash") }}:
            </span>
            <div class="finding-value">
              <ellipsified-copy-text
                :text="device.fileBaselineHash"
                :show-tooltip="true"
                copy-btn-size="24"
                data-testid="fim-finding-baseline-hash"
              />
            </div>
          </div>
          <div v-if="device.fileDetectedHash" class="finding-row ml-8 mb-4">
            <span class="subtitle2 finding-label">
              {{ $t("ticketDetails.fullDetails.fileIntegrityViolation.detectedHash") }}:
            </span>
            <div class="finding-value">
              <ellipsified-copy-text
                :text="device.fileDetectedHash"
                :show-tooltip="true"
                copy-btn-size="24"
                data-testid="fim-finding-detected-hash"
              />
            </div>
          </div>
          <div v-if="getOsLabel(device)" class="finding-row ml-8 mb-4">
            <span class="subtitle2 finding-label">
              {{ $t("ticketDetails.fullDetails.fileIntegrityViolation.os") }}:
            </span>
            <span class="body2" data-testid="fim-finding-os">{{ getOsLabel(device) }}</span>
          </div>
          <div v-if="device.fileDetectionTimestamp" class="finding-row ml-8 mb-4">
            <span class="subtitle2 finding-label">
              {{ $t("ticketDetails.fullDetails.fileIntegrityViolation.detected") }}:
            </span>
            <span class="body2" data-testid="fim-finding-detected-time">
              {{ getFormattedDateTime(device.fileDetectionTimestamp, "MMM, DD YYYY, h:mm A") }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="findingsToLoad > 0" class="load-more-section">
        <div class="coro-link cursor-pointer" @click="loadMoreItems">
          {{ $t("ticketDetails.plusNFindings", { n: findingsToLoad }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { getFormattedDateTime } from "@/_helpers/date";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { type EdrDetectionAffectedDevice, useTicketStore } from "@/_store/tickets/ticket.module";
import { OsType } from "@/constants/devices";

const osTypeLabelKeys: Record<OsType.WINDOWS | OsType.OSX, string> = {
  [OsType.WINDOWS]: "general.osVersion.windows",
  [OsType.OSX]: "general.osVersion.osx",
};

export default defineComponent({
  components: {
    EllipsifiedCopyText,
  },
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup() {
    const ticketStore = useTicketStore();
    const { t } = useI18n();
    const {
      edrAffectedDevices: devices,
      edrAffectedDevicesLoading: loading,
      edrAffectedDevicesTotal: total,
    } = storeToRefs(ticketStore);
    const page = ref(0);
    const expanded = ref<string[]>([]);
    const initialized = ref(false);

    const findingsToLoad = computed(() => {
      return total.value - (devices.value ?? []).length;
    });

    const getDeviceKey = (device: EdrDetectionAffectedDevice) => device.id ?? device.enrollmentCode;

    const isExpanded = (device: EdrDetectionAffectedDevice) =>
      expanded.value.includes(getDeviceKey(device));

    const toggleDevice = (device: EdrDetectionAffectedDevice) => {
      const key = getDeviceKey(device);
      expanded.value = isExpanded(device)
        ? expanded.value.filter((expandedKey) => expandedKey !== key)
        : [...expanded.value, key];
    };

    const getOsLabel = (device: EdrDetectionAffectedDevice) => {
      const labelKey = device.osType ? osTypeLabelKeys[device.osType] : undefined;
      return labelKey ? t(labelKey) : "";
    };

    const getItems = async (virtualScroll = false) => {
      await ticketStore.getEdrAffectedDevices({
        page: page.value,
        virtualScroll,
      });
    };

    const loadMoreItems = () => {
      page.value += 1;
      getItems(true);
    };

    // The first device opens once the initial page arrives; loading further pages must not
    // reopen it after the user has collapsed it.
    watch(
      devices,
      (loadedDevices) => {
        if (initialized.value || !loadedDevices.length) return;
        initialized.value = true;
        expanded.value = [getDeviceKey(loadedDevices[0])];
      },
      { immediate: true }
    );

    onMounted(async () => {
      await getItems();
    });

    return {
      devices,
      loading,
      findingsToLoad,
      loadMoreItems,
      getDeviceKey,
      isExpanded,
      toggleDevice,
      getOsLabel,
      getFormattedDateTime,
    };
  },
});
</script>

<style lang="scss" scoped>
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}

.findings-title {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
}

.finding-row {
  display: flex;
  align-items: center;
}

.finding-label {
  width: 120px;
  min-width: 120px;
}

.finding-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.finding-panel:not(:last-child) {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
}

.finding-panel-header {
  height: 56px;
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: rgb(var(--v-theme-indigo-faint));
}

.rotated {
  transform: rotate(180deg);
}

.load-more-section {
  border-top: 1px solid rgb(var(--v-theme-indigo-pale));
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
